import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import {
    useCreateTeam,
    useUpdateTeam,
    useTeamMobilisationTasks,
    useTeamTechnicalChecklist,
    useSaveTeamMobilisationTasks,
    useSaveTeamTechnicalChecklist,
    teamKeys,
} from '../hooks'
import { useQueryClient } from '@tanstack/react-query'
import { TeamTaskTable } from './TeamTaskTable'
import type { Team } from '@/types/entities'

const teamSchema = z.object({
    teamName: z.string().min(1, 'Team name is required'),
    description: z.string().optional().nullable(),
    mobilisation: z.boolean().optional(),
    task: z.boolean().optional(),
    sms: z.string().optional().nullable(),
    email: z.string().optional().nullable(),
})

type TeamFormData = z.infer<typeof teamSchema>

interface TeamFormDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    team: Team | null
    /** After creating a team, call with the created team (e.g. to open edit dialog with tabs) */
    onCreated?: (team: Team) => void
}

export function TeamFormDialog({ open, onOpenChange, team, onCreated }: TeamFormDialogProps) {
    const { t } = useTranslation()
    const isEditing = !!team
    const createTeam = useCreateTeam()
    const updateTeam = useUpdateTeam()
    const { data: mobilisationTasks = [], isLoading: loadingMob } = useTeamMobilisationTasks(team?.id ?? null)
    const { data: technicalChecklist = [], isLoading: loadingTech } = useTeamTechnicalChecklist(team?.id ?? null)
    const saveMobilisation = useSaveTeamMobilisationTasks()
    const saveTechnical = useSaveTeamTechnicalChecklist()
    const [autoUpdateTasks, setAutoUpdateTasks] = useState(true)
    const queryClient = useQueryClient()

    const form = useForm<TeamFormData>({
        resolver: zodResolver(teamSchema),
        mode: 'onChange',
        defaultValues: {
            teamName: '',
            description: '',
            mobilisation: false,
            task: false,
            sms: '',
            email: '',
        },
    })

    useEffect(() => {
        if (open) {
            if (team) {
                form.reset({
                    teamName: team.teamName || '',
                    description: team.description || '',
                    mobilisation: team.mobilisation || false,
                    task: team.task || false,
                    sms: team.sms || '',
                    email: team.email || '',
                })
            } else {
                form.reset({
                    teamName: '',
                    description: '',
                    mobilisation: false,
                    task: false,
                    sms: '',
                    email: '',
                })
            }
        }
    }, [open, team, form])

    // Refetch task lists when edit dialog opens so we never show another team's or stale data
    useEffect(() => {
        if (open && team?.id) {
            queryClient.invalidateQueries({ queryKey: teamKeys.mobilisationTasks(team.id) })
            queryClient.invalidateQueries({ queryKey: teamKeys.technicalChecklist(team.id) })
        }
    }, [open, team?.id, queryClient])

    const onSubmit = async (data: TeamFormData) => {
        try {
            const submitData = {
                teamName: data.teamName,
                description: data.description?.trim() || null,
                mobilisation: data.mobilisation || false,
                task: data.task || false,
                sms: data.sms?.trim() || null,
                email: data.email?.trim() || null,
            }
            if (isEditing && team) {
                await updateTeam.mutateAsync({ id: team.id, data: submitData })
                onOpenChange(false)
            } else {
                const res = await createTeam.mutateAsync(submitData)
                const created = (res?.data as { data?: Team })?.data ?? (res?.data as Team)
                if (created && typeof created.id === 'number' && onCreated) {
                    onOpenChange(false)
                    onCreated(created as Team)
                } else {
                    onOpenChange(false)
                }
            }
        } catch (error) {
            // Error handling is done in the hooks
        }
    }

    const teamFormFields = (
        <>
            <div className="space-y-2">
                <Label htmlFor="teamName">{t('teams.teamName')} <span className="text-destructive">*</span></Label>
                <Input
                    id="teamName"
                    {...form.register('teamName')}
                    placeholder={t('teams.teamNamePlaceholder')}
                    autoFocus={!isEditing}
                />
                {form.formState.errors.teamName && (
                    <p className="text-sm text-destructive">{form.formState.errors.teamName.message}</p>
                )}
            </div>
            <div className="space-y-2">
                <Label htmlFor="description">{t('teams.description')}</Label>
                <Textarea
                    id="description"
                    {...form.register('description')}
                    placeholder={t('teams.descriptionPlaceholder')}
                    rows={3}
                />
            </div>
            <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                    <Checkbox
                        id="mobilisation"
                        checked={form.watch('mobilisation') || false}
                        onCheckedChange={(checked) => form.setValue('mobilisation', checked === true)}
                    />
                    <Label htmlFor="mobilisation" className="font-normal cursor-pointer">{t('teams.mobilisation')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                    <Checkbox
                        id="task"
                        checked={form.watch('task') || false}
                        onCheckedChange={(checked) => form.setValue('task', checked === true)}
                    />
                    <Label htmlFor="task" className="font-normal cursor-pointer">{t('teams.task')}</Label>
                </div>
            </div>
            <div className="space-y-2">
                <Label htmlFor="sms">{t('teams.sms')}</Label>
                <Textarea id="sms" {...form.register('sms')} placeholder={t('teams.smsPlaceholder')} rows={4} />
                <p className="text-xs text-muted-foreground">{t('teams.smsDescription')}</p>
            </div>
            <div className="space-y-2">
                <Label htmlFor="email">{t('teams.email')}</Label>
                <RichTextEditor
                    value={form.watch('email') || ''}
                    onChange={(html) => form.setValue('email', html, { shouldValidate: true })}
                    placeholder={t('teams.emailPlaceholder')}
                    className="[&_.ProseMirror]:min-h-[120px]"
                />
                <p className="text-xs text-muted-foreground">{t('teams.emailDescription')}</p>
            </div>
        </>
    )

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[960px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {isEditing ? t('teams.editTeam') : t('teams.createTeam')}
                    </DialogTitle>
                    <DialogDescription>
                        {isEditing ? t('teams.editTeamDescription') : t('teams.createTeamDescription')}
                    </DialogDescription>
                </DialogHeader>

                {isEditing ? (
                    <Tabs defaultValue="team" className="w-full">
                        <TabsList className="grid w-full grid-cols-3">
                            <TabsTrigger value="team">{t('teams.tabTeam', 'Team')}</TabsTrigger>
                            <TabsTrigger value="mobilisation">{t('teams.tabMobilisationTasks', 'Mobilisation tasks')}</TabsTrigger>
                            <TabsTrigger value="technical">{t('teams.tabTechnicalChecklist', 'Technical checklist')}</TabsTrigger>
                        </TabsList>
                        <TabsContent value="team" className="space-y-4 pt-4">
                            <form id="team-edit-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                                {teamFormFields}
                            </form>
                        </TabsContent>
                        <TabsContent value="mobilisation" className="pt-4">
                            {loadingMob ? (
                                <p className="text-sm text-muted-foreground py-4">{t('common.loading')}</p>
                            ) : (
                                <TeamTaskTable
                                    key={`mobilisation-${team?.id}`}
                                    tasks={mobilisationTasks}
                                    onSave={(tasks) => team && saveMobilisation.mutate({ teamId: team.id, tasks })}
                                    isSaving={saveMobilisation.isPending}
                                    autoUpdate={autoUpdateTasks}
                                    onAutoUpdateChange={setAutoUpdateTasks}
                                />
                            )}
                        </TabsContent>
                        <TabsContent value="technical" className="pt-4">
                            {loadingTech ? (
                                <p className="text-sm text-muted-foreground py-4">{t('common.loading')}</p>
                            ) : (
                                <TeamTaskTable
                                    key={`technical-${team?.id}`}
                                    tasks={technicalChecklist}
                                    onSave={(tasks) => team && saveTechnical.mutate({ teamId: team.id, tasks })}
                                    isSaving={saveTechnical.isPending}
                                    autoUpdate={autoUpdateTasks}
                                    onAutoUpdateChange={setAutoUpdateTasks}
                                />
                            )}
                        </TabsContent>
                    </Tabs>
                ) : (
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        {teamFormFields}
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={createTeam.isPending}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button type="submit" disabled={createTeam.isPending}>
                                {createTeam.isPending ? t('common.saving') : t('teams.createTeam')}
                            </Button>
                        </DialogFooter>
                    </form>
                )}

                {isEditing && (
                    <DialogFooter className="flex flex-row gap-2 justify-end">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            {t('common.close', 'Close')}
                        </Button>
                        <Button
                            type="submit"
                            form="team-edit-form"
                            disabled={updateTeam.isPending}
                        >
                            {updateTeam.isPending ? t('common.saving') : t('common.save')}
                        </Button>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    )
}
